import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import useScrollReveal from "../hooks/useScrollReveal";
import { speeches, portraits } from "../mock";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Mic, MapPin, Calendar, ArrowRight } from "lucide-react";
import TextToSpeech from "../components/TextToSpeech";
import { ScrollArea } from "../components/ui/scroll-area";

export default function Speech() {
  useScrollReveal();
  const [active, setActive] = useState(null);

  return (
    <div>
      <PageHeader
        eyebrow="Addresses & Lectures"
        title="Speeches"
        subtitle="Memorial lectures, convocation addresses and keynote talks on diplomacy, the United Nations, nuclear affairs and higher education."
        image={portraits.podium}
      />

      <section className="py-20 bg-[#faf8f4]">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 space-y-6">
          {speeches.map((s, i) => (
            <article
              key={s.id}
              className="group bg-white rounded-lg border border-[#eae4d8] p-7 md:p-8 hover:shadow-lg hover:border-[#b8935a] transition-all duration-300 reveal"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-full bg-[#1a2b4a] flex items-center justify-center text-[#e6c98d] flex-shrink-0 group-hover:bg-[#b8935a] group-hover:text-white transition-colors duration-300">
                  <Mic size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-[#1a2b4a] leading-snug">
                    {s.title}
                  </h3>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-[#8a8f9c]">
                    {s.venue && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} /> {s.venue}
                      </span>
                    )}
                    {s.date && (
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={13} /> {s.date}
                      </span>
                    )}
                  </div>
                  <p className="mt-4 text-[#5a6070] leading-relaxed italic">
                    "{s.excerpt}"
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => setActive(s)}
                      className="inline-flex items-center gap-2 text-sm font-medium text-[#b8935a] hover:gap-3 transition-all"
                    >
                      Read full speech <ArrowRight size={15} />
                    </button>
                    <TextToSpeech text={`${s.title}. ${s.full}`} compact />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-[#faf8f4] border-[#eae4d8]">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl md:text-3xl text-[#1a2b4a] leading-snug text-left pr-6">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#8a8f9c]">
                {active.venue && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={13} /> {active.venue}
                  </span>
                )}
                {active.date && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={13} /> {active.date}
                  </span>
                )}
              </div>
              <div className="mt-4">
                <TextToSpeech text={`${active.title}. ${active.full}`} />
              </div>
              <ScrollArea className="mt-4 max-h-[52vh] pr-4">
                {active.full.split("\n\n").map((para, i) => (
                  <p
                    key={i}
                    className="text-[#3a4155] leading-[1.9] text-[15px] mb-4"
                  >
                    {para}
                  </p>
                ))}
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
